import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Chip, Typography } from "@material-ui/core";
import { Page, PageContent, PageHeader, Lookup } from "frame/components";
import NotFound from "./not-found";
import { ChildRoutes } from "./sites-routes";
import { CUSTOMERS_PATH } from "features/customers/actions/types";
import { useTerminology } from "utils";
import SiteForm from "./site-form-container";

const useStyles = makeStyles((theme) => ({
	root: {},
	headerTitle: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
		paddingBottom: theme.spacing(0.5),
	},
	headerRow: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "baseline",
		gap: theme.spacing(1),
		minWidth: 0,
	},
	headerLabel: {
		color: "#999999",
		fontSize: theme.typography.pxToRem(13),
		fontWeight: theme.typography.fontWeightRegular,
		lineHeight: 1.4,
		flexShrink: 0,
	},
	headerValueMedium: {
		color: theme.palette.brand,
		fontSize: theme.typography.pxToRem(14),
		fontWeight: 700,
		lineHeight: 1.35,
		textDecoration: "none",
		"&:hover": {
			textDecoration: "underline",
		},
	},
	headerValueLarge: {
		color: theme.palette.brand,
		fontSize: theme.typography.pxToRem(24),
		fontWeight: 700,
		lineHeight: 1.2,
		letterSpacing: "0.06px",
	},
	chipInactive: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
}));

const Site = ({ site }) => {
	const classes = useStyles();
	const { siteLabel, sitesLabel, customerLabel } = useTerminology();

	const name = (site && site.siteName) || "Not Found";

	const inactive =
		site && String(site.status || "").toLowerCase() === "inactive";

	const pageTitle = (
		<div className={classes.headerTitle} data-cy="site-detail-header">
			{site?.customerId && (
				<div className={classes.headerRow}>
					<Typography
						component="span"
						className={classes.headerLabel}
						data-cy="site-detail-customer-label"
					>
						{customerLabel}
					</Typography>
					<RouterLink
						to={`${CUSTOMERS_PATH}/${site.customerId}`}
						className={classes.headerValueMedium}
						data-cy="site-detail-customer-link"
					>
						<Lookup
							id={site.customerId}
							resource="customers"
							attribute="customerName"
						/>
					</RouterLink>
				</div>
			)}
			<div className={classes.headerRow}>
				<Typography
					component="span"
					className={classes.headerLabel}
					data-cy="site-detail-site-label"
				>
					{siteLabel}
				</Typography>
				<Typography
					component="h1"
					className={classes.headerValueLarge}
					data-cy="site-detail-site-name"
				>
					{name}
				</Typography>
				{inactive && (
					<Chip
						size="small"
						label="Inactive"
						className={classes.chipInactive}
						data-cy="site-detail-status"
					/>
				)}
			</div>
		</div>
	);

	return (
		<Page className={classes.root} title={sitesLabel}>
			<PageHeader title={pageTitle} />
			{site ? (
				<PageContent>
					<ChildRoutes />
					<SiteForm />
				</PageContent>
			) : (
				<PageContent pad>
					<NotFound />
				</PageContent>
			)}
		</Page>
	);
};

Site.propTypes = {
	site: PropTypes.object,
};

export default Site;
